"use client";

import * as React from "react";

import { ORDERUP_PRIVACY_POLICY_URL } from "../../privacy/legalUrls";
import { recordSaleShareOptOut } from "../../privacy/privacySignals";
import { usePrivacySignals } from "../../privacy/usePrivacySignals";
import { cn } from "../../utils/utils";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";

/**
 * The privacy links every customer-facing OrderUp surface shows: the OrderUp
 * Privacy Policy, and — on a surface that loads a merchant's advertising or
 * analytics tags — "Your Privacy Choices", the visitor's "Do Not Sell or Share"
 * opt-out (SOC 2 register R39).
 *
 * Styling comes from the caller (`className`, `linkClassName`, `linkStyle`), so
 * the links sit in a restaurant's themed footer, a checkout card or a dark
 * onboarding bar alike. The opt-out itself is decided in ONE place,
 * `decideMerchantMarketingTags` (`privacy/privacySignals.ts`); this component
 * only records the choice.
 */
export const PRIVACY_LINK_LABELS_EN = {
  /** The links' landmark name (screen readers). */
  nav: "Privacy",
  privacyPolicy: "Privacy Policy",
  choices: "Your Privacy Choices",
  choicesTitle: "Do Not Sell or Share My Personal Information",
  choicesBody:
    "This website can load advertising and analytics tags chosen by the business that runs it, such as the Meta Pixel, Google Analytics and Google Tag Manager. Those tags can share your visit with those companies for advertising, which privacy laws treat as a “sale” or “sharing” of personal information.",
  scope:
    "Opting out turns those tags off on this website, in this browser. It does not change other websites or devices, and it does not affect what you need to place an order.",
  gpcOn:
    "Your browser is sending a Global Privacy Control signal, so these tags are already off for you on this website.",
  optedOut: "You have opted out on this website in this browser. These tags stay off.",
  optOut: "Opt out",
  optBackIn: "Allow these tags again",
  saved: "Saved. The page reloads when you close this, so no tag that was already running keeps running.",
  blocked:
    "Your browser did not let this website save your choice. Turning on Global Privacy Control in your browser's privacy settings opts you out on every visit.",
  readPolicy: "Read the OrderUp Privacy Policy",
};

export type PrivacyLinkLabels = typeof PRIVACY_LINK_LABELS_EN;

export interface PrivacyLinksProps {
  /**
   * Show "Your Privacy Choices". Only a surface that loads merchant marketing
   * tags needs it; elsewhere there is nothing for the opt-out to turn off.
   */
  showChoices?: boolean;
  /** Classes for the wrapping `<nav>`. */
  className?: string;
  /** Classes for each link (and the choices button), e.g. the footer's text colour. */
  linkClassName?: string;
  /** Inline style for each link — for themed footers that colour by value. */
  linkStyle?: React.CSSProperties;
  labels?: Partial<PrivacyLinkLabels>;
}

export function PrivacyLinks({
  showChoices = false,
  className,
  linkClassName,
  linkStyle,
  labels,
}: PrivacyLinksProps) {
  const copy = { ...PRIVACY_LINK_LABELS_EN, ...labels };
  const [choicesOpen, setChoicesOpen] = React.useState(false);
  const linkClasses = cn("underline-offset-4 hover:underline pointer-coarse:min-h-11 inline-flex items-center", linkClassName);

  return (
    <nav aria-label={copy.nav} className={cn("flex flex-wrap items-center gap-x-4 gap-y-1", className)}>
      <a
        href={ORDERUP_PRIVACY_POLICY_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={linkClasses}
        style={linkStyle}
      >
        {copy.privacyPolicy}
      </a>
      {showChoices ? (
        <>
          <button
            type="button"
            className={cn("cursor-pointer bg-transparent p-0 text-left", linkClasses)}
            style={linkStyle}
            onClick={() => setChoicesOpen(true)}
            aria-haspopup="dialog"
          >
            {copy.choices}
          </button>
          <PrivacyChoicesDialog open={choicesOpen} onOpenChange={setChoicesOpen} labels={copy} />
        </>
      ) : null}
    </nav>
  );
}

type SaveState = "idle" | "saved" | "blocked";

/**
 * The opt-out dialog. Records the choice in a first-party cookie, reads it
 * back, and reloads the page on close after an opt-out: a tag that has already
 * run cannot be un-run, so only a fresh page is free of it.
 */
export function PrivacyChoicesDialog({
  open,
  onOpenChange,
  labels,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  labels?: Partial<PrivacyLinkLabels>;
}) {
  const copy = { ...PRIVACY_LINK_LABELS_EN, ...labels };
  const signals = usePrivacySignals();
  const [saveState, setSaveState] = React.useState<SaveState>("idle");

  const handleOpenChange = (next: boolean) => {
    if (!next && saveState === "saved") {
      window.location.reload();
      return;
    }
    if (!next) setSaveState("idle");
    onOpenChange(next);
  };

  const optOut = () => {
    setSaveState(recordSaleShareOptOut(true) ? "saved" : "blocked");
  };

  const optBackIn = () => {
    // Withdrawing the opt-out needs no reload: tags re-decide on the event.
    setSaveState(recordSaleShareOptOut(false) ? "idle" : "blocked");
  };

  const gpc = signals?.globalPrivacyControl === true;
  const optedOut = signals?.saleShareOptOut === true;
  // GPC already decides for this browser, so neither action applies under it.
  const canOptBackIn = !gpc && optedOut && saveState !== "saved";
  const canOptOut = signals !== null && !gpc && !optedOut;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{copy.choicesTitle}</DialogTitle>
          <DialogDescription>{copy.choicesBody}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <p>{copy.scope}</p>
          {gpc ? <p role="status">{copy.gpcOn}</p> : null}
          {canOptBackIn ? <p role="status">{copy.optedOut}</p> : null}
          {saveState === "saved" ? <p role="status">{copy.saved}</p> : null}
          {saveState === "blocked" ? <p role="alert">{copy.blocked}</p> : null}
          <p>
            <a
              href={ORDERUP_PRIVACY_POLICY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4"
            >
              {copy.readPolicy}
            </a>
          </p>
        </div>
        {canOptBackIn || canOptOut ? (
          <DialogFooter>
            {canOptBackIn ? (
              <Button type="button" variant="outline" onClick={optBackIn}>
                {copy.optBackIn}
              </Button>
            ) : null}
            {canOptOut ? (
              <Button type="button" variant="outline" className="font-semibold" onClick={optOut}>
                {copy.optOut}
              </Button>
            ) : null}
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
